// CalendarView.js - FILTROS CORREGIDOS
import React, { useState, useMemo } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  Chip,
  Paper,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  useTheme,
  Tooltip,
  Button,
  Badge,
} from "@mui/material";
import { ChevronLeft, ChevronRight, Today } from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuth } from "../../hooks/useAuth";
import {
  EVENT_TYPE_LABELS,
  EVENT_TYPE_COLORS,
} from "../../utils/models/calendarModel";
import LoadingSpinner from "../ui/LoadingSpinner";

const CalendarView = ({ onEdit, onSelectEvent }) => {
  const theme = useTheme();
  const { user } = useAuth();
  const { data: events, loading } = useCollection("calendarEvents");
  const { data: users } = useCollection("users");

  const [currentDate, setCurrentDate] = useState(new Date());
  const [showAllUsers, setShowAllUsers] = useState(false);
  const [filterType, setFilterType] = useState("all");

  const filteredEvents = useMemo(() => {
    if (!events) return [];
    return events.filter((event) => {
      if (event.isActive === false) return false;
      const matchesType = filterType === "all" || event.eventType === filterType;
      if (!matchesType) return false;
      if (showAllUsers) return true;
      const isCreator = event.createdBy === user.uid;
      const isGeneralEvent = !event.assignedTo || event.assignedTo.length === 0;
      const isAssignedToMe = Array.isArray(event.assignedTo) && event.assignedTo.includes(user.uid);
      return isCreator || isGeneralEvent || isAssignedToMe;
    });
  }, [events, showAllUsers, filterType, user.uid]);

  const daysInMonth = (date) => new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

  const getMonthDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const days = daysInMonth(currentDate);
    const firstDay = new Date(year, month, 1).getDay();
    const firstDayOfWeek = firstDay === 0 ? 6 : firstDay - 1;
    const calendarDays = [];
    const prevMonthDays = daysInMonth(new Date(year, month, 0));
    for (let i = firstDayOfWeek - 1; i >= 0; i--) { calendarDays.push({ date: new Date(year, month - 1, prevMonthDays - i), isCurrentMonth: false }); }
    for (let i = 1; i <= days; i++) { calendarDays.push({ date: new Date(year, month, i), isCurrentMonth: true }); }
    const remainingDays = 42 - calendarDays.length;
    for (let i = 1; i <= remainingDays; i++) { calendarDays.push({ date: new Date(year, month + 1, i), isCurrentMonth: false }); }
    return calendarDays;
  };

  const getEventsForDate = (date) => filteredEvents.filter((event) => new Date(event.startDate).toDateString() === date.toDateString());

  const changeMonth = (increment) => { const newDate = new Date(currentDate); newDate.setMonth(newDate.getMonth() + increment); setCurrentDate(newDate); };
  const goToToday = () => setCurrentDate(new Date());

  const monthNames = ["Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio", "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"];
  const dayNames = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

  const isCurrentMonthAndYear = () => {
    const now = new Date();
    return currentDate.getMonth() === now.getMonth() && currentDate.getFullYear() === now.getFullYear();
  };

  if (loading) return <LoadingSpinner />;
  const calendarDays = getMonthDays();

  return (
    <Box sx={{ width: "100%", maxWidth: "100%", overflow: "hidden" }}>
      <Paper elevation={0} sx={{ p: { xs: 1.5, sm: 2, md: 2.5 }, mb: { xs: 2, sm: 3 }, borderRadius: { xs: "16px", sm: "24px" }, border: "1px solid", borderColor: theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(226,232,240,0.8)", background: theme.palette.mode === "dark" ? "linear-gradient(145deg, rgba(20,20,20,0.95), rgba(30,30,30,0.9))" : "linear-gradient(145deg, #ffffff, #f8fafc)", overflow: "hidden" }}>
        <Stack spacing={1.5}>
          {/* Navegación */}
          <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1}>
            <Stack direction="row" alignItems="center" spacing={{ xs: 0.5, sm: 1 }}
              sx={{ px: { xs: 0.5, sm: 1 }, py: 0.5, borderRadius: "18px", background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.03)", border: "1px solid", borderColor: theme.palette.divider }}>
              <IconButton onClick={() => changeMonth(-1)} size="small" sx={{ borderRadius: "12px" }}><ChevronLeft fontSize="small" /></IconButton>
              <Typography variant="h6" fontWeight={700} sx={{ minWidth: { xs: 140, sm: 180, md: 220 }, textAlign: "center", fontSize: { xs: "0.85rem", sm: "1rem", md: "1.25rem" }, px: { xs: 0.5, sm: 1 } }}>{monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}</Typography>
              <IconButton onClick={() => changeMonth(1)} size="small" sx={{ borderRadius: "12px" }}><ChevronRight fontSize="small" /></IconButton>
            </Stack>
            <Button variant={isCurrentMonthAndYear() ? "outlined" : "contained"} size="small" startIcon={<Today />} onClick={goToToday}
              sx={{ height: { xs: 36, sm: 42 }, px: { xs: 1.5, sm: 2 }, borderRadius: "14px", textTransform: "none", fontWeight: 600, fontSize: { xs: "0.75rem", sm: "0.85rem" }, boxShadow: "none" }}>Hoy</Button>
          </Stack>

          {/* Filtros en grid responsive */}
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr 1fr auto" }, gap: 1, alignItems: "center" }}>
            <FormControl size="small" fullWidth>
              <InputLabel sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>Tipo de evento</InputLabel>
              <Select value={filterType} onChange={(e) => setFilterType(e.target.value)} label="Tipo de evento" sx={{ borderRadius: "12px", height: 44, fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                <MenuItem value="all" sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>Todos</MenuItem>
                {Object.entries(EVENT_TYPE_LABELS).map(([value, label]) => (
                  <MenuItem key={value} value={value} sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}><Box sx={{ width: 10, height: 10, borderRadius: "50%", bgcolor: EVENT_TYPE_COLORS[value] }} />{label}</Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Paper elevation={0} sx={{ px: { xs: 1, sm: 1.5 }, py: 0.5, borderRadius: "12px", display: "flex", alignItems: "center", height: 44, background: theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.03)", border: "1px solid", borderColor: theme.palette.divider }}>
              <FormControlLabel
                sx={{ m: 0 }}
                control={<Switch checked={showAllUsers} onChange={(e) => setShowAllUsers(e.target.checked)} size="small" />}
                label={<Typography variant="body2" fontWeight={500} sx={{ fontSize: { xs: "0.7rem", sm: "0.8rem" } }}>{showAllUsers ? "Todos" : "Solo míos"}</Typography>}
              />
            </Paper>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, height: 44 }}>
              <Chip label={`${filteredEvents.length} eventos`} size="small"
                sx={{ height: 32, borderRadius: "10px", fontWeight: 700, fontSize: { xs: "0.7rem", sm: "0.8rem" }, background: theme.palette.background.paper, border: "1px solid", borderColor: theme.palette.primary.main, color: theme.palette.primary.main }} />
            </Box>
          </Box>
        </Stack>
      </Paper>

      {/* Calendario */}
      <Card elevation={2} sx={{ borderRadius: { xs: 1, sm: 2 }, overflow: "hidden", border: "1px solid", borderColor: theme.palette.divider, background: theme.palette.background.paper, width: "100%", maxWidth: "100%" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", width: "100%" }}>
          {dayNames.map((day, index) => {
            const today = new Date();
            const isTodayColumn = index === (today.getDay() === 0 ? 6 : today.getDay() - 1);
            return (
              <Box key={day} sx={{ p: { xs: 1, sm: 1.5 }, textAlign: "center", backgroundColor: isTodayColumn ? (theme.palette.mode === "dark" ? "rgba(0, 175, 163, 0.2)" : "rgba(0, 95, 101, 0.1)") : (theme.palette.mode === "dark" ? "rgba(0, 175, 163, 0.1)" : "rgba(0, 95, 101, 0.05)"), borderBottom: "2px solid", borderColor: isTodayColumn ? theme.palette.primary.main : theme.palette.divider }}>
                <Typography variant="caption" fontWeight={isTodayColumn ? 700 : 600} fontSize={{ xs: "0.6rem", sm: "0.75rem" }} color={theme.palette.primary.main} sx={{ display: { xs: "none", sm: "block" } }}>{day}</Typography>
                <Typography variant="caption" fontWeight={isTodayColumn ? 700 : 600} fontSize="0.65rem" color={theme.palette.primary.main} sx={{ display: { xs: "block", sm: "none" } }}>{day.charAt(0)}</Typography>
              </Box>
            );
          })}
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", width: "100%" }}>
          {calendarDays.map((day, index) => {
            const dayEvents = getEventsForDate(day.date);
            const isToday = day.date.toDateString() === new Date().toDateString();
            const isWeekend = day.date.getDay() === 0 || day.date.getDay() === 6;
            return (
              <Box key={index} sx={{ minHeight: { xs: 60, sm: 80, md: 100 }, p: { xs: 0.5, sm: 1 }, borderBottom: "1px solid", borderRight: "1px solid", borderColor: theme.palette.divider, position: "relative", backgroundColor: !day.isCurrentMonth ? (theme.palette.mode === "dark" ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)") : isToday ? (theme.palette.mode === "dark" ? "rgba(0, 175, 163, 0.15)" : "rgba(0, 95, 101, 0.08)") : isWeekend ? (theme.palette.mode === "dark" ? "rgba(255,255,255,0.03)" : "rgba(0,0,0,0.01)") : "transparent", "&:nth-of-type(7n)": { borderRight: "none" }, overflow: "hidden", ...(isToday && { boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`, borderRadius: "4px" }) }}>
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    {isToday ? (
                      <Badge overlap="circular" variant="dot" color="primary" sx={{ "& .MuiBadge-dot": { width: { xs: 6, sm: 10 }, height: { xs: 6, sm: 10 } } }}>
                        <Typography variant="caption" fontWeight={700} sx={{ backgroundColor: theme.palette.primary.main, color: "white", width: { xs: 18, sm: 24 }, height: { xs: 18, sm: 24 }, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: { xs: "0.6rem", sm: "0.75rem" } }}>{day.date.getDate()}</Typography>
                      </Badge>
                    ) : (
                      <Typography variant="caption" fontWeight={400} color={!day.isCurrentMonth ? "text.disabled" : "text.primary"} sx={{ width: { xs: 18, sm: 24 }, height: { xs: 18, sm: 24 }, display: "flex", alignItems: "center", justifyContent: "center", fontSize: { xs: "0.6rem", sm: "0.75rem" } }}>{day.date.getDate()}</Typography>
                    )}
                  </Box>
                  {dayEvents.length > 0 && <Chip label={dayEvents.length} size="small" sx={{ height: { xs: 14, sm: 16 }, minWidth: { xs: 14, sm: 16 }, fontSize: { xs: "0.5rem", sm: "0.55rem" }, fontWeight: 600 }} />}
                </Box>
                <Stack spacing={0.3} sx={{ overflow: "hidden" }}>
                  {dayEvents.slice().map((event) => (
                    <Tooltip key={event.id} title={event.title} arrow placement="top">
                      <Box onClick={(e) => { e.stopPropagation(); onSelectEvent(event); }} sx={{ p: { xs: 0.3, sm: 0.5 }, borderRadius: "4px", backgroundColor: event.color || EVENT_TYPE_COLORS[event.eventType], color: "white", cursor: "pointer", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis", maxWidth: "100%" }}>
                        <Typography variant="caption" sx={{ color: "white", fontSize: { xs: "0.5rem", sm: "0.6rem", md: "0.65rem" }, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: "block" }}>{event.title}</Typography>
                      </Box>
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Card>
    </Box>
  );
};

export default CalendarView;